import { useState, useEffect } from "react";
import { apiService } from "../services/api";
import {
    screenConfigRegistry,
    configCache,
    ConfigSource,
    getResolvedScreenConfig
} from "../state/utils/configUtils";
import { ComponentConfig, ComponentType } from "../services/types/ComponentTypes";

interface UseScreenConfigResult {
    config: ComponentConfig[] | null;
    loading: boolean;
    error: string | null;
    source: ConfigSource; // New field to indicate source
}

const formatScreenName = (config: ComponentConfig[]): string | undefined => {
    const defaultTitle = (config[0] as any).title?.toLowerCase().replace(/\s+/g, '_');
    if (!defaultTitle) return undefined;

    const hasSummaryList = (components: ComponentConfig[]): boolean => {
        return components.some(c =>
            c.componentType === ComponentType.SUMMARY_LIST ||
            (c.childComponents && hasSummaryList(c.childComponents))
        );
    };

    const suffix = hasSummaryList(config) ? '_summary' : '_add';
    return `${defaultTitle}${suffix}`;
};

export const useScreenConfig = (screenId: string): UseScreenConfigResult => {
    const [config, setConfig] = useState<ComponentConfig[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [source, setSource] = useState<ConfigSource>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchConfig = async () => {
            // Let's check cache first
            if (configCache[screenId]) {
                if (isMounted) {
                    setConfig(configCache[screenId].config);
                    setSource(configCache[screenId].source);
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            setError(null);
            setSource(null);

            try {
                // 2. Try fetching from API
                // if (["157"].includes(screenId)) {
                //     throw new Error('Fake Error');
                // }
                const response = await apiService.getScreenConfig(screenId);

                if (isMounted) {
                    if (response && response.success && response.data && response.data.settings) {
                        const apiSettings = response.data.settings as any;
                        // API/DB sometimes returns a single screenGroup object instead of an array.
                        // ScreenRenderer/ComponentRenderer expects an array of root components.
                        const apiConfig = Array.isArray(apiSettings) ? apiSettings : [apiSettings];
                        // Not caching API config; we always fetch fresh data.

                        setConfig(apiConfig);
                        setSource('API');
                    } else {
                        // 3. Fallback to static registry
                        const staticConfig = screenConfigRegistry[screenId];
                        if (staticConfig) {
                            console.log({
                                "staticConfig": staticConfig,
                                "screenId": screenId,
                                "name": formatScreenName(staticConfig)
                            });



                            // Not caching static fallback config.

                            setConfig(staticConfig);
                            setSource('LOCAL');
                        } else {
                            setError(`Configuration not found for screen: ${screenId}`);
                            setSource(null);
                        }
                    }
                }
            } catch (err) {
                // 4. Fallback to static registry on API error
                if (isMounted) {
                    const staticConfig = screenConfigRegistry[screenId];
                    console.log({
                        "staticConfig": staticConfig, "screenId": screenId, "name": formatScreenName(staticConfig)

                    });
                    if (staticConfig) {
                        // Cache the fallback result too so we don't retry API failures repeatedly if desired
                        // Or don't cache if you want to retry next time.
                        // Here we cache it to be consistent with success path
                        // Not caching static fallback config – always fetch fresh data next time.
                        setConfig(staticConfig);
                        setSource('LOCAL');
                    } else {
                        setError(`Failed to load configuration for screen: ${screenId}`);
                        setSource(null);
                    }
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        if (screenId) {
            fetchConfig();
        } else {
            setConfig(null);
            setLoading(false);
            setSource(null);
        }

        return () => {
            isMounted = false;
        };
    }, [screenId]);

    return { config, loading, error, source };
};
